import type { IntersectionObserverOptions } from '@react-hook/intersection-observer';
import useIntersectionObserver from '@react-hook/intersection-observer';
import { usePrevious } from '@timelyapp/react';
import React, { useEffect, useRef } from 'react';

interface Props extends React.HTMLProps<HTMLDivElement> {
  /** Intersection options */
  options?: IntersectionObserverOptions;
  /** When the intersection changes state */
  onIntersectingChange: (isIntersecting: boolean, context?: string | Date | number) => void;
  children: React.ReactElement;
  /** Your reference to this element, useful for `onIntersectingChange` handlers */
  context?: string | Date | number;
  /** Wrap `children`, or place itself before / after? Only `wrap` (default) has container that forwards HTML props */
  placement?: 'wrap' | 'top' | 'bottom';
}

/**
 * Wrap this around content you want to detect entering/leaving the view
 * @link https://www.npmjs.com/package/@react-hook/intersection-observer#intersectionobserveroptions
 * */
const IntersectionObserver = (props: Props) => {
  const ref = useRef(null);
  const { onIntersectingChange, options, context, children, placement = 'wrap', ...rest } = props;
  const { isIntersecting } = useIntersectionObserver(ref, options);
  const currentIntersect = usePrevious(isIntersecting);

  useEffect(() => {
    if (currentIntersect !== isIntersecting) {
      onIntersectingChange(isIntersecting, context);
    }
  }, [currentIntersect, isIntersecting, context, onIntersectingChange]);

  if (placement !== 'wrap') {
    return (
      <>
        {placement === 'top' && <div {...rest} ref={ref} />}
        {children}
        {placement === 'bottom' && <div {...rest} ref={ref} />}
      </>
    );
  }

  return (
    <div {...rest} style={{ position: 'relative' }} ref={ref}>
      {children}
    </div>
  );
};

export default IntersectionObserver;
